import React, { useState } from 'react';
import Navbar from '../components/Navbar';
import InnerLeadBanner from '../components/InnerLeadBanner';
import { useApi } from '../context/ApiContext';

const Register = () => {
    const API_BASE_URL = useApi();
    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        email: '',
        username: '',
        password: '',
        confirmPassword: '',
        userType: 'jobSeeker', // Default to job seeker
        agreeToTerms: false
    });
    const [errors, setErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        
        setFormData({
            ...formData,
            [name]: type === 'checkbox' ? checked : value
        });

        // Clear error when user starts typing
        if (errors[name]) {
            setErrors({
                ...errors,
                [name]: ''
            });
        }
    };

    const validateForm = () => {
        const newErrors = {};
        
        if (!formData.firstName.trim()) {
            newErrors.firstName = 'First name is required';
        }
        
        if (!formData.lastName.trim()) {
            newErrors.lastName = 'Last name is required';
        }
        
        if (!formData.email.trim()) {
            newErrors.email = 'Email is required';
        } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
            newErrors.email = 'Email is invalid';
        }
        
        if (!formData.username.trim()) {
            newErrors.username = 'Phone number is required';
        }
        
        if (!formData.password) {
            newErrors.password = 'Password is required';
        } else if (formData.password.length < 6) {
            newErrors.password = 'Password must be at least 6 characters';
        }
        
        if (formData.password !== formData.confirmPassword) {
            newErrors.confirmPassword = 'Passwords do not match';
        }
        
        if (!formData.agreeToTerms) {
            newErrors.agreeToTerms = 'You must agree to the terms and conditions';
        }
        
        setErrors(newErrors);
        
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!validateForm()) {
            return;
        }
        
        setIsSubmitting(true);
        
        try {
            const response = await fetch(`${API_BASE_URL}/register-user/`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    first_name: formData.firstName,
                    last_name: formData.lastName,
                    email: formData.email,
                    username: formData.username,
                    password: formData.password,
                    user_type: formData.userType
                }),
            });
            
            if (response.ok) {
                const result = await response.json();
                console.log('Registration successful:', result);
                alert('Registration successful! Please check your email to verify your account.');
                window.location.href = '/';
            } else {
                const errorData = await response.json();
                
                console.error('Registration failed:', errorData);
                
                // Handle specific backend errors
                if (errorData.email) {
                    setErrors({...errors, email: errorData.email[0]});
                } else if (errorData.username) {
                    setErrors({...errors, username: errorData.username[0]});
                }else {
                    alert('Registration failed. Please try again.');
                }
            }
        } catch (error) {
            console.error('Error during registration:', error);
            alert('An error occurred. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleSocialLogin = (provider) => {
        // Redirect to social login endpoint
        // window.location.href = `${API_BASE_URL}/auth/${provider}`;
        alert(`Social login with ${provider} is still in development.`);
    };

    return (
        <>
            <Navbar/>
            <InnerLeadBanner title='Create Account' />
            
            <section className="contact-section">
                <div className="container">
                    <div className="row">
                        <div className="col-lg-8 mx-auto">
                            <div className="card shadow-sm">
                                <div className="card-body p-5">
                                    <h2 className="text-center mb-4">Create Your Account</h2>
                                    
                                    {/* Social Login Buttons */}
                                    <div className="social-login mb-4">
                                        <div className="row">
                                            <div className="col-md-4 mb-2">
                                                <button 
                                                    className="btn btn-google btn-block"
                                                    onClick={() => handleSocialLogin('google')}
                                                >
                                                    <i className="ti-google"></i> Google
                                                </button>
                                            </div>
                                            <div className="col-md-4 mb-2">
                                                <button 
                                                    className="btn btn-facebook btn-block"
                                                    onClick={() => handleSocialLogin('facebook')}
                                                >
                                                    <i className="ti-facebook"></i> Facebook
                                                </button>
                                            </div>
                                            <div className="col-md-4 mb-2">
                                                <button 
                                                    className="btn btn-linkedin btn-block"
                                                    onClick={() => handleSocialLogin('linkedin')}
                                                >
                                                    <i className="ti-linkedin"></i> LinkedIn
                                                </button>
                                            </div>
                                        </div>
                                        
                                        <div className="divider my-4">
                                            <span className="divider-text">Or register with email</span>
                                        </div>
                                    </div>
                                    
                                    <form onSubmit={handleSubmit}>
                                        <div className="row">
                                            <div className="col-md-6">
                                                <div className="form-group">
                                                    <label htmlFor="firstName">First Name *</label>
                                                    <input
                                                        type="text"
                                                        className={`form-control ${errors.firstName ? 'is-invalid' : ''}`}
                                                        id="firstName"
                                                        name="firstName"
                                                        value={formData.firstName}
                                                        onChange={handleInputChange}
                                                        placeholder="Enter your first name"
                                                    />
                                                    {errors.firstName && (
                                                        <div className="invalid-feedback">{errors.firstName}</div>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="col-md-6">
                                                <div className="form-group">
                                                    <label htmlFor="lastName">Last Name *</label>
                                                    <input
                                                        type="text"
                                                        className={`form-control ${errors.lastName ? 'is-invalid' : ''}`}
                                                        id="lastName"
                                                        name="lastName"
                                                        value={formData.lastName}
                                                        onChange={handleInputChange}
                                                        placeholder="Enter your last name"
                                                    />
                                                    {errors.lastName && (
                                                        <div className="invalid-feedback">{errors.lastName}</div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                        
                                        <div className="form-group">
                                            <label htmlFor="email">Email Address *</label>
                                            <input
                                                type="email"
                                                className={`form-control ${errors.email ? 'is-invalid' : ''}`}
                                                id="email"
                                                name="email"
                                                value={formData.email}
                                                onChange={handleInputChange}
                                                placeholder="Enter your email address"
                                            />
                                            {errors.email && (
                                                <div className="invalid-feedback">{errors.email}</div>
                                            )}
                                        </div>
                                        
                                        <div className="form-group">
                                            <label htmlFor="username">Phone Number *</label>
                                            <input
                                                type="tel"
                                                className={`form-control ${errors.username ? 'is-invalid' : ''}`}
                                                id="username"
                                                name="username"
                                                value={formData.username}
                                                onChange={handleInputChange}
                                                placeholder="Enter your phone number"
                                            />
                                            {errors.username && (
                                                <div className="invalid-feedback">{errors.username}</div>
                                            )}
                                        </div>
                                        
                                        <div className="form-group">
                                            <label>I am a *</label>
                                            <div className="d-flex">
                                                <div className="form-check mr-4">
                                                    <input
                                                        className="form-check-input"
                                                        type="radio"
                                                        name="userType"
                                                        id="jobSeeker"
                                                        value="jobSeeker"
                                                        checked={formData.userType === 'jobSeeker'}
                                                        onChange={handleInputChange}
                                                    />
                                                    <label className="form-check-label" htmlFor="jobSeeker">
                                                        Job Seeker
                                                    </label>
                                                </div>
                                                <div className="form-check">
                                                    <input
                                                        className="form-check-input"
                                                        type="radio"
                                                        name="userType"
                                                        id="employer"
                                                        value="employer"
                                                        checked={formData.userType === 'employer'}
                                                        onChange={handleInputChange}
                                                    />
                                                    <label className="form-check-label" htmlFor="employer">
                                                        Employer
                                                    </label>
                                                </div>
                                            </div>
                                        </div>
                                        
                                        <div className="row">
                                            <div className="col-md-6">
                                                <div className="form-group">
                                                    <label htmlFor="password">Password *</label>
                                                    <input
                                                        type="password"
                                                        className={`form-control ${errors.password ? 'is-invalid' : ''}`}
                                                        id="password"
                                                        name="password"
                                                        value={formData.password}
                                                        onChange={handleInputChange}
                                                        placeholder="Create a password"
                                                    />
                                                    {errors.password && (
                                                        <div className="invalid-feedback">{errors.password}</div>
                                                    )}
                                                    <small className="form-text text-muted">
                                                        Must be at least 6 characters
                                                    </small>
                                                </div>
                                            </div>
                                            <div className="col-md-6">
                                                <div className="form-group">
                                                    <label htmlFor="confirmPassword">Confirm Password *</label>
                                                    <input
                                                        type="password"
                                                        className={`form-control ${errors.confirmPassword ? 'is-invalid' : ''}`}
                                                        id="confirmPassword"
                                                        name="confirmPassword"
                                                        value={formData.confirmPassword}
                                                        onChange={handleInputChange}
                                                        placeholder="Confirm your password"
                                                    />
                                                    {errors.confirmPassword && (
                                                        <div className="invalid-feedback">{errors.confirmPassword}</div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                        
                                        <div className="form-group">
                                            <div className="form-check">
                                                <input
                                                    className={`form-check-input ${errors.agreeToTerms ? 'is-invalid' : ''}`}
                                                    type="checkbox"
                                                    id="agreeToTerms"
                                                    name="agreeToTerms"
                                                    checked={formData.agreeToTerms}
                                                    onChange={handleInputChange}
                                                />
                                                <label className="form-check-label" htmlFor="agreeToTerms">
                                                    I agree to the <a style={{color: 'blue'}} href="/terms-and-confitions" target="_blank">Terms and Conditions</a> and <a style={{color: 'blue'}} href="/privacy-policy" target="_blank">Privacy Policy</a>
                                                </label>
                                                {errors.agreeToTerms && (
                                                    <div className="invalid-feedback d-block">{errors.agreeToTerms}</div>
                                                )}
                                            </div>
                                        </div>
                                        
                                        <div className="form-group">
                                            <button 
                                                type="submit" 
                                                className="btn btn-primary btn-block py-3"
                                                disabled={isSubmitting}
                                            >
                                                {isSubmitting ? 'Creating Account...' : 'Create Account'}
                                            </button>
                                        </div>
                                        
                                        <div className="text-center mt-4">
                                            <p>Already have an account? <a style={{color: 'blue'}} href="/login">Sign In</a></p>
                                        </div>
                                    </form>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
            
            <style jsx>{`
                .contact-section {
                    padding: 50px 0;
                }
                
                .card {
                    border: none;
                    border-radius: 10px;
                }
                
                .social-login .btn {
                    border-radius: 5px;
                    padding: 12px;
                    font-weight: 500;
                }
                
                .btn-google {
                    background-color: #DB4437;
                    color: white;
                    border: none;
                }
                
                .btn-google:hover {
                    background-color: #c23321;
                    color: white;
                }
                
                .btn-facebook {
                    background-color: #4267B2;
                    color: white;
                    border: none;
                }
                
                .btn-facebook:hover {
                    background-color: #365899;
                    color: white;
                }
                
                .btn-linkedin {
                    background-color: #0077B5;
                    color: white;
                    border: none;
                }
                
                .btn-linkedin:hover {
                    background-color: #006097;
                    color: white;
                }
                
                .divider {
                    display: flex;
                    align-items: center;
                    text-align: center;
                }
                
                .divider::before,
                .divider::after {
                    content: '';
                    flex: 1;
                    border-bottom: 1px solid #ddd;
                }
                
                .divider-text {
                    padding: 0 10px;
                    color: #6c757d;
                    font-size: 14px;
                }
                
                .form-check-input {
                    margin-top: 0.23rem;
                }
                
                @media (max-width: 768px) {
                    .card-body {
                        padding: 20px !important;
                    }
                }
            `}</style>
        </>
    );
};

export default Register;